import React, { createContext, useContext, useEffect, useState } from "react";
import { roundMoney } from "@/lib/vat";

const CartContext = createContext(null);
const KEY = "squiddi_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  // SQUIDDI-OPT: carrello laterale - 2026-06-16
  // L'aggiunta apre il pannello laterale (montato in App.js); `lastAdded`
  // serve solo a evidenziare le righe appena inserite.
  const [lastAdded, setLastAdded] = useState(null);   // { items: [...], at }
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const withId = (it) => ({ ...it, cartId: crypto.randomUUID() });

  const addItem = (item, opts = {}) => {
    const line = withId(item);
    setItems((prev) => [...prev, line]);
    if (!opts.silent) { setLastAdded({ items: [line], at: Date.now() }); setDrawerOpen(true); }
    return line;
  };
  // Una riga per colore: lo stesso capo in tre colori entra come tre righe.
  const addItems = (list, opts = {}) => {
    const lines = (list || []).map(withId);
    if (!lines.length) return [];
    setItems((prev) => [...prev, ...lines]);
    if (!opts.silent) { setLastAdded({ items: lines, at: Date.now() }); setDrawerOpen(true); }
    return lines;
  };
  const removeItem = (cartId) => setItems((prev) => prev.filter((i) => i.cartId !== cartId));
  // Annulla rimozione: la riga torna al suo posto.
  const restoreItem = (index, item) => setItems((prev) => {
    const next = [...prev];
    next.splice(Math.max(0, Math.min(index, next.length)), 0, item);
    return next;
  });
  const updateItem = (cartId, patch) =>
    setItems((prev) => prev.map((i) => (i.cartId === cartId ? { ...i, ...patch } : i)));
  // "Aggiorna modifica": sostituisce la riga al suo posto, senza doppioni.
  const replaceItem = (cartId, item) =>
    setItems((prev) => prev.map((i) => (i.cartId === cartId ? { ...item, cartId } : i)));
  const clear = () => setItems([]);
  // Bulk replace — used by the "Riordina" flow (`/orders/:id/reorder`) so
  // the past-order items land in the cart in one shot without duplicates.
  const replaceItems = (list) => setItems((list || []).map(withId));
  const clearLastAdded = () => setLastAdded(null);
  const openCart = () => setDrawerOpen(true);
  const closeCart = () => { setDrawerOpen(false); setLastAdded(null); };

  const count = items.reduce((a, i) => a + (i.total_pieces || 0), 0);
  const total = roundMoney(items.reduce((a, i) => a + (Number(i.estimated_total) || 0), 0));

  return (
    <CartContext.Provider value={{
      items, addItem, addItems, removeItem, restoreItem, updateItem, replaceItem,
      clear, replaceItems, count, total, lastAdded, clearLastAdded,
      drawerOpen, openCart, closeCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
